import { BeefyButton, Card, CheckBox, Explination, Help, Input, Select, Submit, ViewHeader, useGet, useSubmit } from '@enkla-gui'
import React, { useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'

import { icons } from '@config'
import moment from 'moment'

export default function Invoicing() {
  let { id } = useParams()
  const location = useLocation()

  // Get integration ID from query param if present
  const searchParams = new URLSearchParams(location.search)
  const preselectedIntegration = searchParams.get('integration')

  const { data: config, fetch } = useGet({ endpoint: `api/company/${id}/invoicingconfig` })
  const { data: integrations } = useGet({ endpoint: 'api/integration/invoicing' })

  const { register, submit, setValue, watch, getValues, loading, error } = useSubmit({
    endpoint: `api/company/${id}/invoicingconfig`,
    form: config,
    method: 'patch',
    success: fetch
  })

  // Pre-select integration if coming from integration detail page
  useEffect(() => {
    if (preselectedIntegration && integrations && config) {
      setValue('integration_id', preselectedIntegration)
    }
  }, [preselectedIntegration, integrations, config, setValue])

  const getNextGenerateOccurance = () => {
    var content = []
    if ([null, ''].includes(watch('generate_day')))
      return (
        <p>
          Det kommer <b>inte</b> skapas några hyresavier automatiskt.
        </p>
      )
    var generate_day = parseInt(watch('generate_day'))
    var today = parseInt(moment().format('D'))
    var endOfMonth = parseInt(moment().endOf('month').format('D'))
    if (generate_day > endOfMonth) generate_day = endOfMonth

    content.push(
      <p>
        Nästa hyresavi kommer skapas den{' '}
        <b>
          {generate_day}
          {[1, 2].includes(generate_day) ? ':a' : ':e'} {today >= generate_day ? moment().add(1, 'M').format('MMMM') : moment().format('MMMM')}
        </b>
        {today === generate_day && (
          <i><small> (<b>OBS!</b> Eftersom dagens datum sammanfaller med den inställda dagen blir nästa tillfälle först nästa månad.)</small></i>
        )}
      </p>
    )
    if (watch('generate_period') !== null) {
      content.push(
        <p>
          {' '}
          Perioden på hyresavin kommer att börja gälla f.o.m {' '}
          <b>
            {watch('generate_period') === 'CURRENT_MONTH' ? " nuvarande månad " : " nästkommande månad "}
          </b>
          och framåt
        </p>
      )
    }
    return content
  }
  const getNextSendOccurance = () => {
    if ([null, ''].includes(watch('send_day')))
      return (
        <p>
          Det kommer <b>inte</b> skickas några hyresavier automatiskt.
        </p>
      )
    var send_day = parseInt(watch('send_day'))
    var today = parseInt(moment().format('D'))
    var endOfMonth = parseInt(moment().endOf('month').format('D'))
    if (send_day > endOfMonth) send_day = endOfMonth

    return (
      <p>
        Nästa automatiska utskick kommer att göras den{' '}
        <b>
          {send_day}
          {[1, 2].includes(send_day) ? ':a' : ':e'} {today >= send_day ? moment().add(1, 'M').format('MMMM') : moment().format('MMMM')}
        </b>
        {today === send_day && (
          <i><small> (<b>OBS!</b> Eftersom dagens datum sammanfaller med den inställda dagen blir nästa tillfälle först nästa månad.)</small></i>
        )}
      </p>
    )
  }

  return (
    <>
      <ViewHeader
        loading={!config}
        icon={icons.invoice}
        title='Inställningar för hyresavisering'
        subtitle={config?.company.name}
        return={{
          link: `#!/company/${id}`,
          title: config?.company.name
        }}
      />
      <Card
        loading={!integrations}
        buttons={{
          footer: [<Submit onSubmit={submit} text='Spara' sending={loading} />]
        }}>
        <input type='hidden' {...register('company_id')} value={id} />
        <label className='cardlabel mb-3'>
          <i class={icons.invoice} /> Hyresavisering
        </label>
        <div className='row mb-3'>
          <Select
            placeholder='Manuell'
            controls={{ register, setValue, getValues }}
            name='integration_id'
            options={{ required: false }}
            tip="Vad innebär metod för hyresavier?"
            label='Metod för hyresavier'
            error={error}
            values={integrations?.map(integration => {
              return {
                key: `${integration.description} (${integration.name.charAt(0).toUpperCase() + integration.name.toLowerCase().slice(1)})`,
                value: integration.id
              }
            })}
          />
        </div>
        <div className='row mb-3'>
          <Select
            controls={{ register, setValue }}
            name='terms_of_payment'
            options={{ required: false }}
            label='Betalningsvillkor'
            error={error}
            noInitialValue
            values={[
              { key: 'Inaktivt', value: 'INACTIVE' },
              { key: 'Slutet av månaden', value: 'END_OF_MONTH' },
              { key: '0 dagar', value: '0' },
              { key: '10 dagar', value: '10' },
              { key: '15 dagar', value: '15' },
              { key: '20 dagar', value: '20' },
              { key: '30 dagar', value: '30' }
            ]}
          />
          <div className='col'>
            <Input controls={{ register }} name='vat' options={{ required: false }} label='Momsnummer' error={error} />
          </div>
        </div>
        <div className='row mb-3'>
          <div className='col'>
            <Input controls={{ register }} name='plusgiro' options={{ required: false }} label='Plusgiro' error={error} />
          </div>
          <div className='col'>
            <Input controls={{ register }} name='bankgiro' options={{ required: false }} label='Bankgiro' error={error} />
          </div>
        </div>
        <div className='row mb-3'>
          {(watch('integration_id') == null || watch('integration_id') === '') && <div className='col'>
            <div className='mb-3 form-group'>
              <label>
                F-skatt{' '}
                <Explination tip='Om företaget är registrerat för F-skatt visas detta på fakturan' />
              </label>
              <br />
              <CheckBox controls={{ register, getValues, setValue }} name='f_tax' options={{ required: false }} label='Godkänd för F-skatt' />
            </div>
          </div>}
          <div className='col'>
            <div className='mb-3 form-group'>
              <label>Automatisk hyresavisering
                <Explination tip='Hur fungerar automatisk generering av hyresavier?' />
              </label>
              <br />
              <BeefyButton
                _default={{
                  text: 'Ställ in automatisk hyresavisering',
                  icon: icons.toggle(watch('auto_enabled'))
                }}
                hover={{
                  text: 'Ställ in automatisk hyresavisering',
                  icon: icons.toggle(watch('auto_enabled'))
                }}
                type={!watch('auto_enabled') ? 'info' : 'success'}
                callback={() => setValue('auto_enabled', !getValues('auto_enabled'))}
              />
            </div>
          </div>
        </div>
        {watch('auto_enabled') && (
          <>
            <hr className='mt-3' />
            <label className='cardlabel mb-3'>
              <i className='fas fa-sync-alt ' /> Automatisk hantering av hyresavier{' '}
            </label>
            <Help title='Information' type='info'>
              <p>
                Ange vilken dag i månaden hyresavier ska skickas <b>(1-31)</b>.
              </p>
              <p>
                Månader som har färre än <b>31</b> dagar kommer att hanteras på deras sista dag.
              </p>
              <p>
                Hyresavier skickas enbart om de har statusen <b>'Redo att skickas'</b>
              </p>
              <p>
                Efter att den skickats ändras statusen till <b>'Utestående'</b>
              </p>
              {(watch('integration_id') !== null && watch('integration_id') !== '') && (
                <p>
                  <small><em>OBS! Vi rekommenderar att alltid testa integrationen manuellt genom att skapa en hyresavi innan du aktiverar automatiseringen. Detta verifierar att integrationen är korrekt konfigurerad och fungerar som förväntat.</em></small>
                </p>
              )}
            </Help>
            {(watch('integration_id') === null || watch('integration_id') === '') && watch('generate_day')?.length !== 0 &&
              <Help type='warning'>
                <strong>OBS!</strong> Du har valt <strong>manuell metod</strong> för hyresavisering.
                <br />
                <ul style={{ 'marginBottom': '0px' }}>
                  <li>Om du väljer att <strong>kräva attestering</strong> kan du själv ange både <em>OCR-nummer</em> och <em>fakturanummer</em> för alla avier efter att systemet har genererat dem.</li>
                  <li>Om du <strong>inte</strong> kräver attestering genererar systemet automatiskt ett <em>fakturanummer</em>, medan <em>OCR-nummer</em> lämnas tomt.</li>
                </ul>
              </Help>
            }
            <div className='row mb-3'>
              <Input
                controls={{ register }}
                name='generate_day'
                placeholder='Inaktivt'
                options={{ required: false }}
                label='Hyresavier ska skapas automatiskt den'
                error={error}
                type='number'
              />
              <Input
                controls={{ register }}
                name='send_day'
                placeholder='Inaktivt'
                options={{ required: false }}
                label='Hyresavier ska skickas automatiskt den'
                error={error}
                type='number'
              />
            </div>
            <div className='row mb-3'>
              <div className='col-6'>
                <Select
                  controls={{ register, setValue }}
                  disabled={watch('generate_day') === null || watch('generate_day')?.length === 0}
                  name='generate_period'
                  options={{ required: false }}
                  label='Hyresavierna avser'
                  error={error}
                  values={[
                    { key: 'Nuvarande månad', value: 'CURRENT_MONTH' },
                    { key: 'Nästkommande månad', value: 'NEXT_MONTH' }
                  ]}
                />
              </div>
              <div className='col-6'>
                <div className='mb-3 form-group'>
                  <label>
                    Attestera hyresavier innan de skickas{' '}
                    <Explination tip='Hyresavierna måste godkännas innan de skickas iväg. Avier som kräver attestering samlas i menyn under Hyresavier > Attestering' />
                  </label>
                  <br />
                  <CheckBox controls={{ register, getValues, setValue }} name={`auto_require_review`} options={{ required: false }} label='Kräv attestering' />
                </div>
              </div>
            </div>
            <div className='row'>
              <Help type='info'>
                {getNextGenerateOccurance()}
                {getNextSendOccurance()}
              </Help>
            </div>
          </>
        )}
      </Card>
    </>
  )
}
